import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import type { ModuleKey } from '../constants/modules';

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  requireMfa: boolean;
  enabledModules: ModuleKey[];
  userCount: number;
  createdAt: string;
}

interface Props {
  onSelect: (tenant: Tenant) => void;
}

export default function ClientsListPage({ onSelect }: Props) {
  const { t, i18n } = useTranslation();
  const [tenants, setTenants]   = useState<Tenant[]>([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError]       = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/admin/tenants');
      setTenants(data);
    } catch {
      setError(t('tenants.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="page">
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>{t('tenants.title')}</h1>
          <p style={{ fontSize: 13, color: 'var(--gray)' }}>{t('tenants.count', { count: tenants.length })}</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          style={{ padding: '8px 18px', background: 'var(--dark)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
        >
          {t('tenants.newClient')}
        </button>
      </div>

      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div style={{ color: 'var(--gray)', fontSize: 13 }}>{t('tenants.loading')}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {tenants.map(tenant => (
            <div
              key={tenant.id}
              onClick={() => onSelect(tenant)}
              style={{
                background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)',
                padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer',
                opacity: tenant.active ? 1 : 0.6,
              }}
            >
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--green-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                🏢
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{tenant.name}</div>
                <div style={{ fontSize: 11, color: 'var(--gray)' }}>
                  /{tenant.slug} · {t('tenants.users', { count: tenant.userCount })} · {t('tenants.since')} {new Date(tenant.createdAt).toLocaleDateString(i18n.language.startsWith('es') ? 'es-MX' : 'en-US', { month: 'short', year: 'numeric' })}
                </div>
              </div>
              <span style={{ fontSize: 11, color: 'var(--gray)' }}>
                {t('admin.detail.modulesCount', { count: tenant.enabledModules.length, total: 8 })}
              </span>
              <span style={{
                fontSize: 11, padding: '2px 10px', borderRadius: 99, fontWeight: 600,
                background: tenant.active ? 'var(--green-bg)' : '#f5f5f3',
                color: tenant.active ? 'var(--green-dk)' : 'var(--gray)',
              }}>
                {tenant.active ? t('tenants.status.active') : t('tenants.status.inactive')}
              </span>
              <span style={{ fontSize: 16, color: 'var(--gray)' }}>→</span>
            </div>
          ))}

          {tenants.length === 0 && (
            <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--gray)', fontSize: 13 }}>
              {t('tenants.noClients')}
            </div>
          )}
        </div>
      )}

      {showForm && (
        <TenantFormModal
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function TenantFormModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', slug: '', adminEmail: '', adminPassword: '', adminName: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const slugify = (s: string) => s.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  const handleNameChange = (name: string) => {
    setForm(f => ({ ...f, name, slug: f.slug || slugify(name) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const body: Record<string, string> = { name: form.name, slug: form.slug };
      if (form.adminEmail) {
        body.adminEmail    = form.adminEmail;
        body.adminPassword = form.adminPassword;
        if (form.adminName) body.adminName = form.adminName;
      }
      await api.post('/admin/tenants', body);
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.error ?? t('tenants.form.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'var(--white)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 440, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>{t('tenants.form.title')}</h2>
        <p style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 20 }}>{t('tenants.form.subtitle')}</p>

        {error && <div className="error-message" style={{ marginBottom: 14 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>{t('tenants.form.company')}</div>
          <div className="field">
            <label>{t('tenants.form.clientName')}</label>
            <input value={form.name} onChange={e => handleNameChange(e.target.value)} required placeholder={t('tenants.form.clientNamePlaceholder')} />
          </div>
          <div className="field">
            <label>{t('tenants.form.slug')}</label>
            <input value={form.slug} onChange={e => setForm({ ...form, slug: slugify(e.target.value) })} required placeholder={t('tenants.form.slugPlaceholder')} pattern="^[a-z0-9-]+" />
          </div>

          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '18px 0 10px' }}>{t('tenants.form.initialAdmin')}</div>
          <div className="field">
            <label>{t('tenants.form.adminName')}</label>
            <input value={form.adminName} onChange={e => setForm({ ...form, adminName: e.target.value })} placeholder={t('tenants.form.adminNamePlaceholder')} />
          </div>
          <div className="field">
            <label>{t('tenants.form.email')}</label>
            <input type="email" value={form.adminEmail} onChange={e => setForm({ ...form, adminEmail: e.target.value })} placeholder={t('tenants.form.emailPlaceholder')} />
          </div>
          <div className="field">
            <label>{t('tenants.form.tempPassword')}</label>
            <input type="password" value={form.adminPassword} onChange={e => setForm({ ...form, adminPassword: e.target.value })} placeholder={t('tenants.form.passwordPlaceholder')} minLength={form.adminEmail ? 8 : 0} />
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: 10, border: '1px solid var(--border)', borderRadius: 8, background: 'transparent', fontSize: 13, cursor: 'pointer' }}>{t('tenants.form.cancel')}</button>
            <button type="submit" disabled={saving} className="primary-button" style={{ flex: 1 }}>{saving ? t('tenants.form.creating') : t('tenants.form.create')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
